import { useState } from "react";
import { Send, CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const TOPICS = ["General", "Find a Program", "Become a Partner", "Monthly Supporter", "Sponsor a Program", "Volunteering", "Media Permissions"];

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: TOPICS[0], message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await api.post("/contact", form);
      setStatus("success");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div
        className="bg-white border border-slate-200 rounded-2xl p-10 text-center flex flex-col items-center shadow-xl shadow-purple-100"
        data-testid="contact-success"
      >
        <div className="w-14 h-14 rounded-full bg-emerald-100 border border-emerald-300 flex items-center justify-center">
          <CheckCircle2 className="w-7 h-7 text-emerald-600" />
        </div>
        <h3 className="font-display text-2xl font-bold text-slate-900 mt-5">Message sent!</h3>
        <p className="text-slate-500 text-sm mt-2 max-w-xs">
          Thanks for reaching out — we&apos;ll get back to you soon.
        </p>
        <button
          onClick={() => {
            setForm({ name: "", email: "", phone: "", topic: TOPICS[0], message: "" });
            setStatus("idle");
          }}
          data-testid="contact-reset-btn"
          className="btn-outline px-6 py-2.5 text-sm mt-6"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-5 shadow-xl shadow-purple-100" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="field-label" htmlFor="cf-name">Name</label>
          <input
            id="cf-name"
            className="field"
            value={form.name}
            onChange={set("name")}
            required
            data-testid="contact-name-input"
            placeholder="Your name"
          />
        </div>
        <div>
          <label className="field-label" htmlFor="cf-email">Email</label>
          <input
            id="cf-email"
            type="email"
            className="field"
            value={form.email}
            onChange={set("email")}
            required
            data-testid="contact-email-input"
            placeholder="you@email.com"
          />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="field-label" htmlFor="cf-phone">Phone (optional)</label>
          <input
            id="cf-phone"
            type="tel"
            className="field"
            value={form.phone}
            onChange={set("phone")}
            data-testid="contact-phone-input"
            placeholder="(314) 000-0000"
          />
        </div>
        <div>
          <label className="field-label" htmlFor="cf-topic">Topic</label>
          <select id="cf-topic" className="field" value={form.topic} onChange={set("topic")} data-testid="contact-topic-select">
            {TOPICS.map((t) => (
              <option key={t} value={t} className="bg-white">
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="field-label" htmlFor="cf-message">Message</label>
        <textarea
          id="cf-message"
          className="field min-h-[130px] resize-y"
          value={form.message}
          onChange={set("message")}
          required
          data-testid="contact-message-input"
          placeholder="How can Y.E.S. help, or how would you like to help?"
        />
      </div>
      {status === "error" && (
        <p className="text-sm text-red-600" data-testid="contact-error">
          {error}
        </p>
      )}
      <button type="submit" disabled={status === "sending"} className="btn-primary w-full py-3.5" data-testid="contact-submit-btn">
        {status === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        {status === "sending" ? "Sending..." : "Send Message"}
      </button>
    </form>
  );
};

export default ContactForm;